import React, { ReactNode, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LAUNCHER_REDIRECT } from '../../constants';
import { useAuthContext } from '../../contexts/AuthContext/AuthContext';
import getRoutePathFromLauncherUri from './getRoutePathFromLauncherUri';
import { idFromUri } from '../../utils/pointer';
import { useAnalytics } from '../../contexts/AnalyticsContext/useAnalytics';
import { useAppFeaturesContext } from '../../contexts/AppFeaturesContext/AppFeaturesContext';
import { useNavigateWithHistory } from '../useNavigateWithHistory';

const LauncherRedirect: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigateWithHistory();

  const { user } = useAuthContext();
  const { setUtmParameters } = useAnalytics();
  const { mode } = useAppFeaturesContext();

  const launcherUri = useMemo(
    () => searchParams.get(LAUNCHER_REDIRECT),
    [searchParams]
  );

  const [id, redirectPath, isPlayerUri] = useMemo(
    () => [
      launcherUri && idFromUri(launcherUri.replace('/player', '')),
      getRoutePathFromLauncherUri(launcherUri),
      !!launcherUri?.includes('/player'),
    ],
    [launcherUri]
  );

  const shouldRedirect: boolean = useMemo(
    () => !!user && !user.isSubAccount && !!redirectPath?.length && !!id,
    [id, redirectPath?.length, user]
  );

  useEffect(() => {
    if (!shouldRedirect) return;

    if (redirectPath && id) {
      setUtmParameters(mode, isPlayerUri, id);

      setSearchParams(prev => {
        prev.delete(LAUNCHER_REDIRECT);

        return prev;
      });

      navigate(redirectPath);
    }
  }, [
    id,
    isPlayerUri,
    mode,
    navigate,
    redirectPath,
    setSearchParams,
    setUtmParameters,
    shouldRedirect,
  ]);

  return !shouldRedirect ? children : null;
};

export default LauncherRedirect;
